import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const KEY = 'tistore_privacy_choice_v1';
const VERSION = '2026-10-18';
const COUNTER = 106268900;
const readChoice = () => {
  try { const saved = JSON.parse(localStorage.getItem(KEY)); return saved?.version === VERSION && saved.expires > Date.now() && ['accepted','rejected'].includes(saved.choice) ? saved : null; } catch { return null; }
};
const clearTracking = () => {
  try { Object.keys(localStorage).filter(k => k.startsWith('ph_') || k.startsWith('_ym')).forEach(k => localStorage.removeItem(k)); } catch {}
  document.cookie.split(';').forEach(cookie => {
    const name = cookie.split('=')[0].trim();
    if (!name.startsWith('_ym') && !name.startsWith('ph_')) return;
    ['', location.hostname, '.' + location.hostname, '.tistoreapp.ru'].forEach(domain => { document.cookie = `${name}=; Max-Age=0; path=/;${domain ? ' domain=' + domain + ';' : ''}`; });
  });
};
export default function CookieConsent() {
  const { pathname } = useLocation();
  const [choice, setChoice] = useState(readChoice);
  const [open, setOpen] = useState(!choice);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const allowed = useRef(false);
  const publicPage = pathname === '/' || pathname.startsWith('/art/');
  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener('tistore-cookie-settings', show);
    // Remove identifiers left by the retired tracker without loading its SDK.
    try { Object.keys(localStorage).filter(k => k.startsWith('ph_')).forEach(k => localStorage.removeItem(k)); } catch {}
    return () => window.removeEventListener('tistore-cookie-settings', show);
  }, []);
  useEffect(() => {
    allowed.current = choice?.choice === 'accepted' && publicPage;
    if (!allowed.current) {
      if (window.ym) window.ym(COUNTER, 'destruct');
      if (choice?.choice !== 'accepted') clearTracking();
      return;
    }
    let cancelled = false;
    const start = () => {
      if (cancelled || !allowed.current) return;
      const cleanUrl = location.origin + pathname;
      window.ym(COUNTER, 'init', { defer: true, webvisor: false, clickmap: false, trackLinks: false, accurateTrackBounce: true });
      window.ym(COUNTER, 'hit', cleanUrl, { title: document.title, referer: '' });
    };
    if (document.getElementById('tistore-metrika')?.dataset.ready) start();
    else {
      if (!window.ym) { window.ym = function () { (window.ym.a = window.ym.a || []).push(arguments); }; window.ym.l = Date.now(); }
      let script = document.getElementById('tistore-metrika');
      if (!script) { script = document.createElement('script'); script.id = 'tistore-metrika'; script.async = true; script.src = 'https://mc.yandex.ru/metrika/tag.js'; document.head.appendChild(script); }
      script.onload = () => { script.dataset.ready = 'true'; start(); };
    }
    return () => { cancelled = true; if (window.ym) window.ym(COUNTER, 'destruct'); };
  }, [choice, pathname, publicPage]);
  const choose = async value => {
    setBusy(true); setError('');
    // A refusal takes effect immediately, even if the connection fails.
    if (value === 'rejected') { allowed.current = false; if (window.ym) window.ym(COUNTER, 'destruct'); clearTracking(); }
    try {
      const response = await fetch('/api/privacy/consent', { method: 'POST', headers: { 'Content-Type':'application/json' }, body: JSON.stringify({ choice: value, version: VERSION }) });
      if (!response.ok) throw new Error();
      const receipt = await response.json();
      const saved = { choice: value, version: VERSION, receipt: receipt.id, expires: Date.now() + 180 * 86400000 };
      try { localStorage.setItem(KEY, JSON.stringify(saved)); } catch {}
      setChoice(saved); setOpen(false);
    } catch {
      if (value === 'rejected') { const saved = { choice: value, version: VERSION, expires: Date.now() + 180 * 86400000 }; try { localStorage.setItem(KEY, JSON.stringify(saved)); } catch {} setChoice(saved); setOpen(false); }
      else setError('Не удалось сохранить согласие. Аналитика остаётся выключенной. Попробуйте ещё раз.');
    } finally { setBusy(false); }
  };
  if (pathname.startsWith('/admin') || !open) return null;
  return <section role="region" aria-label="Настройки cookies" className="fixed z-[60] bottom-4 left-4 right-4 md:left-auto md:w-[480px] rounded-2xl border border-white/25 bg-[#1E1E1E] text-white p-5 shadow-2xl max-h-[80dvh] overflow-y-auto">
    <h2 className="text-xl mb-2">Ваш выбор cookies</h2>
    <p className="text-sm leading-relaxed">Необходимое хранилище помогает работать корзине и входу. Яндекс Метрику для статистики посещений включим только с вашего разрешения. Отказ не мешает покупкам.</p>
    <p className="text-sm my-3"><Link to="/cookie-consent" className="underline">Условия согласия на аналитику</Link> · <Link to="/privacy_policy" className="underline">Политика</Link></p>
    {error && <p role="alert" className="text-sm mb-3">{error}</p>}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      <button disabled={busy} onClick={() => choose('rejected')} className="rounded-xl border border-white/50 px-4 py-3 text-sm disabled:opacity-50">Только необходимые</button>
      <button disabled={busy} onClick={() => choose('accepted')} className="rounded-xl bg-[#AD2831] px-4 py-3 text-sm disabled:opacity-50">Разрешить аналитику</button>
    </div>
    {choice && <button onClick={() => setOpen(false)} className="text-sm underline mt-3">Закрыть без изменений</button>}
  </section>;
}
